import type { SceneContent } from '../../../presentation/types';
import { spokenList } from '../../../presentation/catalogue';

export const mintCore: SceneContent = {
  id: 'mint-core',
  shortLabel: 'MINT',
  title: 'MINT — One Intelligence Foundation',
  seconds: 55,
  brand: 'mint',
  bridgeIn: "At the centre of Moderta's work is one intelligence platform. It starts with a problem every organisation recognises.",
  atmosphere: (step) =>
    step <= 1 ? { pattern: 'stream', density: 0.3 - step * 0.1, opacity: 0.07, cameraZ: 13.6 }
    : { pattern: 'inward', density: 0.3, opacity: 0.07, cameraZ: 14.2 },
  script: {
    title: 'MINT — One Intelligence Foundation',
    onScreen: ['DOCUMENTS · DATA · RESEARCH · SYSTEMS · REPORTS · KNOWLEDGE · OPERATIONS · EXTERNAL INFORMATION', 'INFORMATION EVERYWHERE.', 'UNDERSTANDING NOWHERE.', 'MINT', 'INFORMATION → CONTEXT → CONNECTION → REASONING → INTELLIGENCE', 'ONE INTELLIGENCE FOUNDATION.'],
    visual: 'Lanes of real-looking information stream across the screen from eight kinds of source. The flood slows and nothing adds up. The information converges and MINT appears in its path: five stages light in turn, and something usable leaves the other side. The diagram dims behind the closing statement.',
    narration: [
      'Start with something every organisation recognises. Documents, data, research, systems, reports, knowledge, operations, and everything arriving from outside.',
      'Information is everywhere. Understanding is not. Most of it just flows past, disconnected from everything else.',
      'MINT sits in the path of that information.',
      'It takes information, understands its context, connects it to everything else it knows, reasons over it, and turns it into intelligence someone can use.',
      'That is MINT. Not one application, but one intelligence foundation.',
    ],
    presenterNotes: [
      'Information streams past. "Information everywhere."',
      'The flood slows. "Understanding nowhere."',
      'Everything converges into MINT. Name it.',
      'The stages light. Read them in one breath.',
      '"One intelligence foundation." Pause: this is the idea the next scenes build on.',
    ],
  },
};

export const mintIntelligence: SceneContent = {
  id: 'mint-intelligence',
  shortLabel: 'REASONING',
  title: 'MINT — How Intelligence Works',
  seconds: 45,
  brand: 'mint',
  bridgeIn: 'So what actually happens between a question and a useful answer?',
  atmosphere: () => ({ pattern: 'ambient', density: 0.16, opacity: 0.05, cameraZ: 14.8 }),
  script: {
    title: 'MINT — How Intelligence Works',
    onScreen: ['"Where should we focus next?"', 'CONTEXT', 'KNOWLEDGE', 'RETRIEVAL', 'SPECIALISED ANALYSIS', 'EVIDENCE', 'REASONING', 'RESPONSE', 'NOT JUST ANSWERS.', 'CONTEXT-AWARE INTELLIGENCE.'],
    visual: 'A question types itself at the top. A bright trace descends through seven reasoning layers; four specialist nodes exchange signals; evidence is gathered; the answer lands with its sources attached. The diagram dims for two statements.',
    narration: [
      'Take a simple question. Where should we focus next?',
      'A generic tool would answer it straight away. MINT first works out the context: who is asking, what they are responsible for, and what they already know. Then it looks at the organisation\'s own knowledge and retrieves what is relevant.',
      'Then several specialised parts of the system work on it together. Each one looks at the question from a different angle, and each one has to bring evidence.',
      'Only then does it reason across all of that and respond, with the evidence still attached.',
      'So the point is not just getting an answer.',
      'It is intelligence that understands the context it is working in.',
    ],
    presenterNotes: [
      'The question appears. Read it out loud.',
      'The question descends: context, knowledge, retrieval. "It works out the context first."',
      'Specialised nodes collaborate; evidence appears. "Several specialists, each bringing evidence."',
      'Reasoning, then the response with its sources. Point at the evidence tags.',
      '"Not just answers."',
      '"Context-aware intelligence." Pause before moving on.',
    ],
  },
};

export const mintSpecialisation: SceneContent = {
  id: 'mint-specialisation',
  shortLabel: 'FAMILY',
  title: 'MINT — Specialised Intelligence',
  seconds: 30,
  brand: 'mint',
  bridgeIn: 'Once intelligence understands context, it can be specialised.',
  atmosphere: (step) => ({ pattern: 'orbit', density: 0.2, opacity: 0.06, cameraZ: step >= 1 ? 14 : 14.6 }),
  script: (context) => {
    const names = context.products.filter((p) => p.tier === 'specialisation').map((p) => p.name);
    const list = spokenList(names);
    return {
      title: 'MINT — Specialised Intelligence',
      onScreen: ['ONE FOUNDATION.', 'SPECIALISED INTELLIGENCE.', ...names],
      visual: `MINT alone at the top. ${names.length === 1 ? 'A branch grows' : 'Branches grow'} out of it, one per specialisation, each carrying its name and what it is for. One more branch grows without a name.`,
      narration: [
        'MINT is the foundation.',
        `On top of it, the same intelligence is specialised for particular domains: ${list}. Each one changes the context MINT works in, not the foundation underneath.`,
        'And the family is not closed. The same foundation can be shaped around domains that do not exist yet.',
      ],
      presenterNotes: [
        'MINT alone. "One foundation."',
        `Branches grow. Name ${names.length === 1 ? 'it' : 'them'}.`,
        'An unnamed branch appears. Do not name it; it is the point.',
      ],
    };
  },
};

export const mintDirections: SceneContent = {
  id: 'mint-directions',
  shortLabel: 'DIRECTIONS',
  title: 'MINT — One Foundation, Many Directions',
  seconds: 25,
  brand: 'mint',
  bridgeIn: 'Step back, and look at the MINT family as a whole.',
  atmosphere: (step) => ({ pattern: step >= 1 ? 'orbit' : 'ambient', density: 0.22, opacity: 0.07, cameraZ: 14.8 }),
  script: {
    title: 'MINT — One Foundation, Many Directions',
    onScreen: ['ONE FOUNDATION.', 'MANY DIRECTIONS.'],
    visual: 'The frame pulls back to show the whole MINT family together. Unnamed branches grow in every direction from the MINT core, with no labels on them.',
    narration: [
      'Put together, this is the MINT family.',
      'And each specialisation makes the next one easier, because none of them starts from zero.',
      'One foundation.',
      'Many directions.',
    ],
    presenterNotes: [
      'The frame pulls back. "This is the MINT family."',
      'Unnamed branches grow. "None of them starts from zero."',
      '"One foundation."',
      '"Many directions." Leave the unnamed branches unnamed.',
    ],
  },
};
